import { gsap } from "gsap";
import type { FC } from "react";
import { useRef, useState, useEffect, } from "react";
import styled from "styled-components";



export declare type UpDownProps = {
	direction?: "up" | "down";
	/** The initial direction of the arrow */
	initialDirection?: "up" | "down";
	/** Callback function called when the button is clicked */
	onClick?: ({ direction }: { direction: "up" | "down" }) => void;
	/** Size of the button in pixels */
	size?: number;
	/** Whether the button is disabled */
	disabled?: boolean;
};



/**
 * A circular button component with an arrow that can point up or down
 * and animates with a twist when clicked to change direction
 * @param params - The component props
 * @param params.initialDirection - The initial direction of the arrow ("up" | "down")
 * @param params.onClick - Callback function called when button is clicked
 * @param params.size - Size of the button in pixels
 * @param params.disabled - Whether the button is disabled
 * @returns A circular button with animated arrow
 */
export const UpDown: FC<UpDownProps> = ({
	direction,
	initialDirection = "up",
	onClick,
	size = 40,
	disabled = false,
}) => {

	const [autoDirection, setAutoDirection] = useState<"up" | "down">(initialDirection);
	const arrowRef = useRef<SVGSVGElement>(null);

	const handleClick = () => {
		if(disabled) return;
		const newDirection = direction === undefined ? autoDirection === "up" ? "down" : "up" : direction === "up" ? "down" : "up";
		const rotation = newDirection === "down" ? 180 : 0;
		gsap.to(arrowRef.current, {
			rotation,
			duration: 0.3,
			ease: "power2.inOut",
		});

		if(!direction) {
			setAutoDirection(newDirection);
		}

		onClick?.({ direction: newDirection });
	};


	useEffect(() => {
		if(direction === "down" || initialDirection === "down") {
			gsap.to(arrowRef.current, {
				rotation: 180,
				duration: 0,
				ease: "power2.inOut",
			});
		}
	}, []);

	return (
		<StyledButton
			onClick={handleClick}
			disabled={disabled}
			$size={size}
			type="button"
		>
			<ArrowIcon
				ref={arrowRef}
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M7 14L12 9L17 14"
					stroke="currentColor"
					strokeWidth="4"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</ArrowIcon>
		</StyledButton>
	);
};



const StyledButton = styled.button<{ $size: number }>`
	width: ${({ $size }) => $size}px;
	height: ${({ $size }) => $size}px;
	border-radius: 50%;
	border: 2px solid var(--border-primary);
	background: var(--primary);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;
	color: black;
	opacity: 0.65;

	&:hover:not(:disabled) {
		background: var(--primary);
		border-color: var(--border-hover);
		transform: scale(1.35);
		opacity: 1;
		transition: all 0.2s ease;
	}

	&:active:not(:disabled) {
		transform: scale(0.95);
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	&:focus {
		outline: 2px solid var(--focus-ring);
		outline-offset: 2px;
	}
`;

const ArrowIcon = styled.svg`
	pointer-events: none;
`;
